import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, downloadFile, gbp } from "@/lib/api";
import { AdminTitle } from "./AdminLayout";
import { BTN } from "./AdminInvoices";
import { EMPTY_SALE, SaleForm, saleFromRecord } from "./SaleForm";
import { extrasTotal, gbp2 } from "./SaleExtras";

const download = (no, fmt) => downloadFile(`/admin/sales/${no}/${fmt}`, `Cirencester-4x4-Vehicle-Sale-Agreement-${no}.${fmt}`).catch((e) => toast.error(apiError(e)));
const downloadHandover = (no) => downloadFile(`/admin/sales/${no}/handover/pdf`, `Cirencester-4x4-Handover-Checklist-${no}.pdf`).catch((e) => toast.error(apiError(e)));

const BLANKS = [
  ["sale-agreement", "Vehicle-Sale-Agreement", "Vehicle Sale Agreement", "The same agreement and terms of sale, left blank to print and complete by hand at a viewing or handover."],
  ["handover-checklist", "Handover-Checklist", "Handover Checklist", "One page to tick at collection or delivery: keys, V5C/2 slip, DVLA, documents, odometer, fuel, damage and signatures."],
];

const BlankSale = () => (
  <section className="mt-8 border border-stone bg-paper p-6">
    <h2 className="mb-4 font-serif text-2xl">Blank documents</h2>
    <div className="space-y-3">
      {BLANKS.map(([id, file, title, text]) => (
        <div key={id} className="flex flex-wrap items-center justify-between gap-4 border border-stone p-4" data-testid={`doc-${id}`}>
          <div className="max-w-2xl"><p className="text-sm font-semibold">{title}</p><p className="text-sm text-charcoal/75">{text}</p></div>
          <div className="flex gap-2">
            {[["Word", "docx"], ["PDF", "pdf"]].map(([l, ext]) => (
              <a key={ext} href={`/docs/Cirencester-4x4-${file}.${ext}`} download data-testid={`doc-${id}-${ext}`} className={BTN}>Download {l}</a>
            ))}
          </div>
        </div>
      ))}
    </div>
  </section>
);

const SaleRow = ({ s, onEdit, onInvoice, onDelete }) => (
  <li className={`flex flex-wrap items-center justify-between gap-3 border-b border-stone py-4 ${s.status === "Cancelled" ? "opacity-60" : ""}`} data-testid={`sale-row-${s.sale_no}`}>
    <div className="min-w-0">
      <p className="text-sm font-semibold">{s.sale_no} <span className="font-normal text-charcoal/70">· {s.sale_date} · {s.status}</span></p>
      <p className="text-sm">{s.buyer_name} · {[s.make, s.model, s.derivative].filter(Boolean).join(" ")}{s.registration ? ` (${s.registration})` : ""}</p>
      <p className="text-sm text-forest"><strong>{gbp2(s.price + extrasTotal(s.extras))}</strong>{s.extras?.length ? ` · incl. ${s.extras.length} extra${s.extras.length > 1 ? "s" : ""}` : ""}{s.deposit ? ` · deposit ${gbp(s.deposit)}` : ""}{s.warranty_included ? " · warranty included" : ""}</p>
    </div>
    <div className="flex flex-wrap gap-2">
      <button type="button" className={BTN} onClick={() => download(s.sale_no, "pdf")} data-testid={`sale-pdf-${s.sale_no}`}>Agreement PDF</button>
      <button type="button" className={BTN} onClick={() => download(s.sale_no, "docx")} data-testid={`sale-docx-${s.sale_no}`}>Word</button>
      <button type="button" className={BTN} onClick={() => downloadHandover(s.sale_no)} data-testid={`sale-handover-${s.sale_no}`}>Handover checklist</button>
      <button type="button" className={BTN} onClick={() => onEdit(s)} data-testid={`sale-edit-${s.sale_no}`}>Edit</button>
      <button type="button" className={BTN} onClick={() => onInvoice(s)} data-testid={`sale-invoice-${s.sale_no}`}>Create invoice</button>
      <button type="button" className="border border-red-800 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-red-800 hover:bg-red-800 hover:text-paper" onClick={() => onDelete(s)} data-testid={`sale-delete-${s.sale_no}`}>Delete</button>
    </div>
  </li>
);

export default function AdminSales() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null); // null | {initial, saleNo}
  const navigate = useNavigate();
  const load = useCallback(() => {
    api.get("/admin/sales").then((r) => setList(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(apiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);
  const remove = async (s) => {
    if (!window.confirm(`Delete sale agreement ${s.sale_no} for ${s.buyer_name}? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/sales/${s.sale_no}`);
      toast.success(`Sale agreement ${s.sale_no} deleted`);
      if (editing?.saleNo === s.sale_no) setEditing(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };
  return (
    <div data-testid="admin-sales">
      <AdminTitle title="Sales">
        {!editing && <button type="button" onClick={() => setEditing({ initial: EMPTY_SALE, saleNo: null })} className="btn btn-primary !py-3" data-testid="sale-new">Record a sale</button>}
      </AdminTitle>
      <p className="mb-6 text-sm text-charcoal/75">Vehicles you sell to private buyers. Each gets an agreement number (VSA-year-101 onwards) and a pre-filled Vehicle Sale Agreement in PDF and Word, with your terms of sale. Private: only signed-in admins can see it.</p>
      {editing && (
        <div className="mb-8">
          <SaleForm key={editing.saleNo || "new"} initial={editing.initial} saleNo={editing.saleNo}
            onSaved={(s) => { setEditing(null); load(); download(s.sale_no, "pdf"); }} onCancel={() => setEditing(null)} />
        </div>
      )}
      <section className="border border-stone bg-paper px-6 py-2">
        {list.length ? (
          <ul>{list.map((s) => <SaleRow key={s.sale_no} s={s} onDelete={remove} onInvoice={(x) => navigate("/admin/invoices", { state: { fromSale: x } })}
            onEdit={(x) => { setEditing({ initial: saleFromRecord(x), saleNo: x.sale_no }); window.scrollTo({ top: 0, behavior: "smooth" }); }} />)}</ul>
        ) : <p className="py-6 text-sm text-charcoal/70" data-testid="sale-empty">No sale agreements yet.</p>}
      </section>
      <BlankSale />
    </div>
  );
}
